/**
 * Hook to fetch and aggregate user's collateral and borrow positions across all subaccounts
 *
 * This provides the foundation for viewing complete user positions by giving
 * a complete view of where all collateral is located and what debt exists on each subaccount.
 */

import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { useELendsList } from '@/domain/euler/elends/useELendInfo'
import { getSubAccountAddress, FALLBACK_SUBACCOUNT_RANGE } from '@/domain/euler/shared/subAccounts'
import { useSubaccountStore } from '@/domain/euler/subaccounts'
import { Percentage } from '@/domain/types/NumericValues'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { eulerVaultConnectorAbi } from 'abis/evm/eulerVaultConnector'
import { useMemo } from 'react'
import { type Address, formatUnits } from 'viem'
import { useAccount, useReadContract, useReadContracts } from 'wagmi'
import type { BorrowPosition, CollateralPosition, UserCollateralState } from './types'

const SECONDS_PER_YEAR = 365.25 * 24 * 60 * 60

export interface UseUserCollateralStateParams {
  /** Specific vault addresses to query (if empty, queries all known vaults) */
  vaultAddresses?: Address[]
  userAddress?: Address
  chainId?: number
  enabled?: boolean
}

export interface UseUserCollateralStateResult {
  state: UserCollateralState | null
  isLoading: boolean
  error: Error | null
  evcAddress: Address | undefined
}

/**
 * Fetches complete user collateral state across all subaccounts
 */
export function useUserCollateralState({
  vaultAddresses: specificVaults,
  userAddress,
  chainId = E_LEND_CHAIN_ID,
  enabled = true,
}: UseUserCollateralStateParams = {}): UseUserCollateralStateResult {
  const { address: connectedAddress } = useAccount()
  const address = userAddress || connectedAddress

  // Get active subaccount IDs from the global discovery cache
  const { activeSubaccountIds, isDiscovering } = useSubaccountStore()

  // Get all known vaults from indexer
  const { vaults: elendVaults, isLoading: isLoadingVaults } = useELendsList(chainId)

  // Use specific vaults if provided, otherwise all known vaults
  const vaultAddresses = specificVaults || elendVaults.map((v) => v.address)

  // Get EVC address
  const firstVaultAddress = elendVaults[0]?.address
  const { data: evcAddressData, isLoading: isLoadingEvc } = useReadContract({
    address: firstVaultAddress,
    abi: eVaultImplementationAbi,
    functionName: 'EVC',
    chainId,
    query: {
      enabled: !!firstVaultAddress && enabled,
      staleTime: 60_000,
    },
  })
  const evcAddress = evcAddressData as Address | undefined

  // Generate subaccount addresses to query
  // Include fallback range (0-15) for safety, plus any higher active subaccounts from discovery
  const subAccountAddresses = useMemo(() => {
    if (!address) return []
    const fallbackIds = Array.from({ length: FALLBACK_SUBACCOUNT_RANGE }, (_, i) => i)
    const idsToQuery = [...new Set([...fallbackIds, ...activeSubaccountIds])].sort((a, b) => a - b)
    return idsToQuery.map((id) => ({
      subAccountId: id,
      address: getSubAccountAddress(address, id),
    }))
  }, [address, activeSubaccountIds])

  // Step 1: Query EVC for controllers and collaterals for all subaccounts
  const evcContracts = useMemo(() => {
    if (!evcAddress || !address || !enabled) return []
    return subAccountAddresses.flatMap(({ address: subAddr }) => [
      {
        address: evcAddress,
        abi: eulerVaultConnectorAbi,
        functionName: 'getControllers' as const,
        args: [subAddr] as const,
        chainId,
      },
      {
        address: evcAddress,
        abi: eulerVaultConnectorAbi,
        functionName: 'getCollaterals' as const,
        args: [subAddr] as const,
        chainId,
      },
    ])
  }, [evcAddress, address, enabled, subAccountAddresses, chainId])

  const { data: evcData, isLoading: isLoadingEvc2 } = useReadContracts({
    contracts: evcContracts as any,
    query: {
      enabled: enabled && !!address && !!evcAddress && evcContracts.length > 0,
      // Reduced from 30s to 5s to ensure controller/debt data is fresh after transactions
      // This prevents showing stale positions in Quick Borrow after repay/borrow operations
      staleTime: 5_000,
    },
  })

  // Parse EVC data into controllers and collaterals maps
  const { controllers, enabledCollaterals } = useMemo(() => {
    const controllers = new Map<number, Address | null>()
    const enabledCollaterals = new Map<number, Address[]>()

    if (!evcData || !address) {
      // Initialize with empty data for all subaccounts we're querying
      for (const { subAccountId } of subAccountAddresses) {
        controllers.set(subAccountId, null)
        enabledCollaterals.set(subAccountId, [])
      }
      return { controllers, enabledCollaterals }
    }

    // Safety check: ensure data length matches expected to handle keepPreviousData race conditions
    const expectedLength = subAccountAddresses.length * 2
    if (evcData.length !== expectedLength) {
      for (const { subAccountId } of subAccountAddresses) {
        controllers.set(subAccountId, null)
        enabledCollaterals.set(subAccountId, [])
      }
      return { controllers, enabledCollaterals }
    }

    for (let i = 0; i < subAccountAddresses.length; i++) {
      const { subAccountId } = subAccountAddresses[i]!
      const controllersResult = (evcData[i * 2]?.result as Address[]) || []
      const collateralsResult = (evcData[i * 2 + 1]?.result as Address[]) || []

      controllers.set(subAccountId, controllersResult[0] || null)
      enabledCollaterals.set(subAccountId, collateralsResult)
    }

    return {
      controllers,
      enabledCollaterals,
    }
  }, [evcData, address, subAccountAddresses])

  // Step 2: Query vault balances and debt for all subaccounts
  const vaultContracts = useMemo(() => {
    if (!address || !enabled || vaultAddresses.length === 0) return []

    const contracts: any[] = []

    // For each vault, query share price and interest rate once
    for (const vaultAddress of vaultAddresses) {
      contracts.push({
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'convertToAssets' as const,
        args: [BigInt(10 ** 18)] as const,
        chainId,
        _vault: vaultAddress,
        _type: 'sharePrice',
      })
      contracts.push({
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'interestRate' as const,
        chainId,
        _vault: vaultAddress,
        _type: 'interestRate',
      })
    }

    // For each subaccount, query balanceOf and debtOf for each vault
    for (const { subAccountId, address: subAddr } of subAccountAddresses) {
      for (const vaultAddress of vaultAddresses) {
        contracts.push({
          address: vaultAddress,
          abi: eVaultImplementationAbi,
          functionName: 'balanceOf' as const,
          args: [subAddr] as const,
          chainId,
          _vault: vaultAddress,
          _subAccountId: subAccountId,
          _type: 'balance',
        })
        contracts.push({
          address: vaultAddress,
          abi: eVaultImplementationAbi,
          functionName: 'debtOf' as const,
          args: [subAddr] as const,
          chainId,
          _vault: vaultAddress,
          _subAccountId: subAccountId,
          _type: 'debt',
        })
      }
    }

    return contracts
  }, [address, enabled, vaultAddresses, subAccountAddresses, chainId])

  const { data: vaultData, isLoading: isLoadingVaultData } = useReadContracts({
    contracts: vaultContracts.map(({ _vault, _subAccountId, _type, ...c }) => c) as any,
    query: {
      enabled: enabled && !!address && vaultContracts.length > 0,
      staleTime: 5_000,
    },
  })

  // Step 3: Build the complete state
  const state = useMemo<UserCollateralState | null>(() => {
    if (!address || !vaultData || vaultAddresses.length === 0) return null

    // Safety check: ensure vaultData length matches expected to handle keepPreviousData race conditions
    // Expected: vaultAddresses.length * 2 (share price + interest rate per vault)
    //         + subAccountAddresses.length * vaultAddresses.length * 2 (balance + debt per vault per subaccount)
    const expectedVaultDataLength = vaultAddresses.length * 2 + subAccountAddresses.length * vaultAddresses.length * 2
    if (vaultData.length !== expectedVaultDataLength) return null

    // Create vault metadata map from indexer
    const vaultMetadata = new Map(elendVaults.map((v) => [v.address.toLowerCase(), v]))

    // Parse vault-level data (share prices and interest rates)
    const vaultInfo = new Map<string, { sharePrice: bigint; interestRate: bigint }>()
    let dataIndex = 0
    for (const vaultAddress of vaultAddresses) {
      const sharePrice = (vaultData[dataIndex]?.result as bigint) || BigInt(10 ** 18)
      const interestRate = (vaultData[dataIndex + 1]?.result as bigint) || 0n
      vaultInfo.set(vaultAddress.toLowerCase(), { sharePrice, interestRate })
      dataIndex += 2
    }

    // Parse per-subaccount data
    const collaterals: CollateralPosition[] = []
    const borrows: BorrowPosition[] = []
    let totalCollateralUsd = 0
    let totalDebtUsd = 0

    for (const { subAccountId } of subAccountAddresses) {
      const enabledForSubAccount = enabledCollaterals.get(subAccountId) || []

      for (const vaultAddress of vaultAddresses) {
        const balance = (vaultData[dataIndex]?.result as bigint) || 0n
        const debt = (vaultData[dataIndex + 1]?.result as bigint) || 0n
        dataIndex += 2

        const vaultKey = vaultAddress.toLowerCase()
        const info = vaultInfo.get(vaultKey)
        const metadata = vaultMetadata.get(vaultKey)

        if (!info || !metadata) continue

        const decimals = metadata.assetDecimals || 18
        const price = metadata.assetPrice || 0
        const assets = (balance * info.sharePrice) / BigInt(10 ** 18)
        const assetsNum = Number(formatUnits(assets, decimals))
        const valueUsd = assetsNum * price

        // Add collateral position if balance > 0
        if (balance > 0n) {
          const isEnabled = enabledForSubAccount.some((c) => c.toLowerCase() === vaultKey)
          // Get supply APY from indexer (totalApy is already a percentage value like 5.25 for 5.25%)
          // Keep track of whether we have valid totalApy data (not null)
          const hasTotalApy = metadata.totalApy !== null && metadata.totalApy !== undefined
          const supplyApyValue = metadata.totalApy ?? 0
          // Get intrinsic APY from indexer (e.g., staking yield, PT yield)
          // Only use intrinsic APY if we also have totalApy - otherwise decompounding would produce incorrect values
          const intrinsicApyValue = hasTotalApy ? metadata.intrinsicApy?.apy ?? undefined : undefined
          // Calculate lending APY by decompounding: if yields compound, extract base lending APY
          // Formula: ((1 + totalApy) / (1 + intrinsicApy) - 1) - inverse of compounding
          // Only decompound if we have valid totalApy AND positive intrinsic APY
          const lendingApyValue =
            hasTotalApy && intrinsicApyValue !== undefined && intrinsicApyValue > 0
              ? ((1 + supplyApyValue / 100) / (1 + intrinsicApyValue / 100) - 1) * 100
              : supplyApyValue
          collaterals.push({
            vaultAddress,
            underlyingAsset: metadata.asset as Address,
            symbol: metadata.assetSymbol || 'UNKNOWN',
            decimals,
            shares: balance,
            assets,
            valueUsd,
            sharePrice: info.sharePrice,
            subAccountId,
            isEnabled,
            supplyApy: Percentage(supplyApyValue / 100, true, true),
            lendingApy: lendingApyValue,
            intrinsicApy: intrinsicApyValue,
          })
          totalCollateralUsd += valueUsd
        }

        // Add borrow position if debt > 0
        if (debt > 0n) {
          const debtNum = Number(formatUnits(debt, decimals))
          const debtUsd = debtNum * price

          // Calculate APY from interest rate
          const ratePerSecond = Number(formatUnits(info.interestRate, 27))
          const apy = ((1 + ratePerSecond) ** SECONDS_PER_YEAR - 1) * 100

          borrows.push({
            vaultAddress,
            underlyingAsset: metadata.asset as Address,
            symbol: metadata.assetSymbol || 'UNKNOWN',
            decimals,
            debt,
            debtUsd,
            subAccountId,
            borrowApy: Percentage(apy / 100, true, true),
          })
          totalDebtUsd += debtUsd
        }
      }
    }

    // Find available subaccounts (no controller) from the queried subaccounts
    const availableSubAccounts: number[] = []
    for (const { subAccountId } of subAccountAddresses) {
      if (!controllers.get(subAccountId)) {
        availableSubAccounts.push(subAccountId)
      }
    }

    return {
      collaterals,
      borrows,
      totalCollateralUsd,
      totalDebtUsd,
      controllers,
      enabledCollaterals,
      availableSubAccounts,
    }
  }, [address, vaultData, vaultAddresses, elendVaults, subAccountAddresses, controllers, enabledCollaterals])

  // Only consider loading if we have an address and are enabled
  const isLoading =
    !!address && enabled && (isLoadingVaults || isLoadingEvc || isLoadingEvc2 || isLoadingVaultData || isDiscovering)

  return {
    state,
    isLoading,
    error: null,
    evcAddress,
  }
}
